import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input } from '@angular/core';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';

export type BadgeColor =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'analog'
  | 'contrast';
type BadgeAppearance = 'filled' | 'tonal' | 'basic';
type BadgeSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

@Component({
  selector: 'span[app-badge]',
  imports: [CommonModule, SkeletonLoaderComponent],
  templateUrl: './badge.component.html',
  styleUrls: ['./badge.component.scss'],
  standalone: true,
})
export class BadgeComponent {
  @Input() appearance: BadgeAppearance = 'tonal';
  @Input() color: BadgeColor;
  @Input() size: BadgeSize = 'sm';
  @Input() loading = false;
  @HostBinding('class.badge') readonly isBadge = true;
  @HostBinding('class.badge--loading') get isLoading() {
    return this.loading;
  }

  // appearance
  @HostBinding('class.badge--filled') get isFilled() {
    return this.appearance === 'filled';
  }

  @HostBinding('class.badge--tonal') get isTonal() {
    return this.appearance === 'tonal';
  }

  @HostBinding('class.badge--basic') get isBasic() {
    return this.appearance === 'basic';
  }

  // sizes
  @HostBinding('class.badge--xs') get isXs() {
    return this.size === 'xs';
  }
  @HostBinding('class.badge--sm') get isSm() {
    return this.size === 'sm';
  }
  @HostBinding('class.badge--md') get isMd() {
    return this.size === 'md';
  }
  @HostBinding('class.badge--lg') get isLg() {
    return this.size === 'lg';
  }
  @HostBinding('class.badge--xl') get isXl() {
    return this.size === 'xl';
  }

  // colors
  @HostBinding('class.badge--primary') get isPrimary() {
    return this.color === 'primary';
  }

  @HostBinding('class.badge--success') get isSuccess() {
    return this.color === 'success';
  }

  @HostBinding('class.badge--warning') get isWarning() {
    return this.color === 'warning';
  }

  @HostBinding('class.badge--danger') get isDanger() {
    return this.color === 'danger';
  }

  @HostBinding('class.badge--info') get isInfo() {
    return this.color === 'info';
  }

  @HostBinding('class.badge--neutral') get isNeutral() {
    return this.color === 'neutral';
  }

  @HostBinding('class.badge--analog') get isAnalog() {
    return this.color === 'analog';
  }

  @HostBinding('class.badge--contrast') get isContrast() {
    return this.color === 'contrast';
  }
}
